import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useCallback, useState } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { Icon, VerticalEllipsis } from 'chatie-design-system/icon';

import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';
import EFinderImage from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';
import Pad from '@components/layout/Pad';

import IcLockSmall from '@public/icons/ic_lock_small.svg';
import IcPlay from '@public/icons/ic_play.svg';

import { IChatCharacter } from '@model';
import { linearGradient, multilineEllipsis } from '@styles/css';
import { formatNumberMillion } from '@utils/chat';

const cardContainerCSS = (size: CardSizeObject) => (theme: Theme) =>
  css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: ${size.height}px;
    border-radius: ${Radius.small}px;
    box-shadow: 3px -2px 20px 3px #0000000d;
    overflow: hidden;

    .imageContainer {
      height: ${size.imageHeight}px;
      background-color: ${theme.var.white};
      position: relative;

      .gradient {
        position: absolute;
        width: 100%;
        height: 40px;
        top: 0;
        z-index: 1;
        ${linearGradient('top', 'rgba(255, 255, 255, 0)', 'rgba(0, 0, 0, 0.2)')}
      }

      .icon-btn {
        position: absolute;
        top: 0px;
        right: 0px;
        z-index: 10;
        padding: 0;
        padding: 8px 10px;
        margin: 0;
      }

      .playCount {
        position: absolute;
        width: 100%;
        top: 0;
        display: flex;
        gap: ${Spacing.xxxsmall}px;
        padding: 10px;
        ${Typography.body11};
        color: ${theme.var.white};
        z-index: 10;

        svg path {
          fill: currentcolor;
        }
      }

      .image {
        width: 100%;
        height: ${size.imageHeight}px;
        background-color: ${theme.var.gray200};
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 9px ${Spacing.medium}px;

      .name {
        ${Typography.body12};
        ${multilineEllipsis(1)}
      }
      .age {
        ${size.infoFontSize === 11 ? Typography.body11 : Typography.body12};
        margin-top: ${Spacing.xxsmall}px;
        margin-bottom: ${Spacing.xsmall}px;
        color: ${theme.var.gray500};
        ${multilineEllipsis(1)}
      }
      .desc {
        ${Typography.body12};
        font-weight: 600;
        ${multilineEllipsis(1)}
      }
    }
  `;

export type ProfileInfoCardSizeType = 'w166' | 'w128';

export interface CardSizeObject {
  width: number;
  height: number;
  imageHeight: number;
  infoFontSize: number;
}

export const ProfileInfoCardSize = {
  w166: {
    width: 166,
    height: 216,
    imageHeight: 140,
    infoFontSize: 12,
  },
  w128: {
    width: 128,
    height: 199,
    imageHeight: 126,
    infoFontSize: 11,
  },
};

interface ProfileInfoCardProps {
  data: IChatCharacter;
  size: ProfileInfoCardSizeType;
  isMyPage: boolean;
  handleActionSheet?: (value: ProfileInfoCardType) => void;
  handleSelectCharacter: (character: IChatCharacter) => void;
  handleCreateChat: (characterId: number) => void;
  handleNavigateEdit: (character: IChatCharacter) => void;
}

export type ProfileInfoCardType = 'modify' | 'delete' | 'detail' | 'report';

const ProfileInfoCard = ({
  data,
  size = 'w128',
  isMyPage,
  handleActionSheet,
  handleSelectCharacter,
  handleCreateChat,
  handleNavigateEdit,
}: ProfileInfoCardProps) => {
  const theme = useTheme();
  const { information } = data;
  const { t } = useTranslation();

  const isTemp = data.status === 'DRF';
  const isPrivate = data.status === 'PRV';

  const getActionSheetValue =
    useCallback((): ActionSheetOption<ProfileInfoCardType>[] => {
      if (isMyPage) {
        if (isTemp) {
          return [
            { label: t('수정하기'), value: 'modify' },
            { label: t('삭제하기'), value: 'delete', isDanger: true },
          ];
        } else {
          return [
            { label: t('캐릭터 상세로 가기'), value: 'detail' },
            { label: t('수정하기'), value: 'modify' },
            { label: t('삭제하기'), value: 'delete', isDanger: true },
          ];
        }
      } else {
        return [
          { label: t('캐릭터 상세로 가기'), value: 'detail' },
          { label: t('신고하기'), value: 'report', isDanger: true },
        ];
      }
      return [];
    }, [isMyPage, isTemp, t]);

  const [actions] = useState<ActionSheetOption<ProfileInfoCardType>[]>(
    getActionSheetValue()
  );
  return (
    <div
      css={cardContainerCSS(ProfileInfoCardSize[size])}
      onClick={() =>
        isTemp ? handleNavigateEdit(data) : handleCreateChat(data.id)
      }
    >
      <div className="imageContainer">
        <Pad className="gradient" />
        <div>
          <div className="playCount">
            {isTemp ? (
              <p>임시저장</p>
            ) : (
              <>
                <SvgIcon icon={isPrivate ? IcLockSmall : IcPlay} />
                <p>{formatNumberMillion(data.stat.message_count)}</p>
              </>
            )}
          </div>
          {handleActionSheet && (
            <ActionSheet
              name={`character-card-action-${data.id}`}
              options={actions}
              onSelect={(value) => handleActionSheet(value)}
            >
              {(open) => (
                <button
                  className="icon-btn"
                  onClick={(e) => {
                    open(e);
                    handleSelectCharacter(data);
                  }}
                >
                  <Icon
                    icon={VerticalEllipsis}
                    color={theme.var.white}
                    size={Sizes.small}
                  />
                </button>
              )}
            </ActionSheet>
          )}
        </div>
        {data?.profile_image && data?.profile_image.key ? (
          <EFinderImage image={data.profile_image} alt={'profile_img'} />
        ) : (
          <div className="image" />
        )}
      </div>
      <div className="info">
        <h6 className="name">{information.name || '캐릭터 이름'}</h6>
        <p className="age">{`${information.age || 'N'}세 | ${
          information.job || '직업'
        }`}</p>
        <p className="desc">{data.greeting || '캐릭터 한마디'}</p>
      </div>
    </div>
  );
};

export default ProfileInfoCard;
